import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@/lib/api/baseQuery';

import type {
  CheckoutRequest,
  CheckoutResponse,
  Invoice,
  PlansResponse,
  PortalResponse,
  SubscriptionView,
} from '../types/billing.types';

/**
 * RTK Query surface for feature 10 — Billing.
 *
 * Every endpoint that touches a payment provider returns a clean 503 when the
 * corresponding provider's keys are absent. The frontend reflects this via
 * the `stripe_enabled` / `crypto_enabled` flags on `/billing/plans` —
 * checkout buttons render disabled in that state.
 */
export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery,
  tagTypes: ['Subscription', 'Plans', 'Invoices'],
  endpoints: (builder) => ({
    plans: builder.query<PlansResponse, void>({
      query: () => ({ url: '/billing/plans' }),
      providesTags: ['Plans'],
    }),
    subscription: builder.query<SubscriptionView, void>({
      query: () => ({ url: '/billing/subscription' }),
      providesTags: ['Subscription'],
    }),
    invoices: builder.query<Invoice[], { limit?: number; offset?: number } | void>({
      query: (arg) => ({
        url: '/billing/invoices',
        params: arg
          ? {
              limit: arg.limit ?? 25,
              offset: arg.offset ?? 0,
            }
          : undefined,
      }),
      providesTags: ['Invoices'],
    }),
    createCheckout: builder.mutation<CheckoutResponse, CheckoutRequest>({
      query: (body) => ({ url: '/billing/checkout', method: 'POST', body }),
      invalidatesTags: ['Subscription'],
    }),
    createPortal: builder.mutation<PortalResponse, { return_url: string }>({
      query: (body) => ({ url: '/billing/portal', method: 'POST', body }),
    }),
  }),
});

export const {
  usePlansQuery,
  useSubscriptionQuery,
  useInvoicesQuery,
  useCreateCheckoutMutation,
  useCreatePortalMutation,
} = billingApi;
